
import { Interview } from "@/types/interview";
import { InterviewFetchService } from "./interviewFetchService";
import { InterviewUpdateService } from "./interviewUpdateService";
import { InterviewPublicationService } from "./publication";
import { InterviewCreationService } from "./creation";

/**
 * Facade service that coordinates interview management operations
 */
export class InterviewManagementService {
  /**
   * Create a new interview directly for production page
   */
  static async createDirectInterview(interviewData: {
    intervieweeName: string;
    title: string;
    company: string;
    context: string;
    contextUrl?: string;
    question: string;
    answer: string;
    tags: string[];
    photoUrl?: string;
    publication_status?: string;
    isSponsored?: boolean;
    sponsorCompany?: string;
    sponsorLogoUrl?: string;
    sponsorLogoPosition?: 'profile' | 'article' | 'both';
  }): Promise<{success: boolean, error?: string, id?: string}> {
    try {
      console.log(`Creating direct interview for: ${interviewData.intervieweeName}`);
      console.log('Interview data includes contextUrl:', interviewData.contextUrl || 'No contextUrl');
      
      // Call the InterviewCreationService to create the interview
      const result = await InterviewCreationService.createInterview(interviewData);
      
      if (!result) {
        console.error('Failed to create interview - null result');
        return { success: false, error: 'Failed to create interview' };
      }
      
      return { success: true, id: result };
    } catch (error) {
      console.error("Error creating interview:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetch all pending interviews
   */
  static async getPendingInterviews(): Promise<Interview[]> {
    return InterviewFetchService.getPendingInterviews();
  }

  /**
   * Fetch all published interviews
   */
  static async getPublishedInterviews(): Promise<Interview[]> {
    return InterviewFetchService.getPublishedInterviews();
  }

  /**
   * Update interview details
   */
  static async updateInterview(interviewId: string, updateData: Partial<Interview>): Promise<boolean> {
    return InterviewUpdateService.updateInterview(interviewId, updateData);
  }

  /**
   * Publish an interview
   */
  static async publishInterview(interviewId: string): Promise<boolean> {
    return InterviewPublicationService.publishInterview(interviewId);
  }

  /**
   * Reject an interview
   */
  static async rejectInterview(interviewId: string): Promise<boolean> {
    return InterviewPublicationService.rejectInterview(interviewId);
  }

  /**
   * De-publish an interview (move back to pending)
   */
  static async depublishInterview(interviewId: string): Promise<boolean> {
    return InterviewPublicationService.depublishInterview(interviewId);
  }

  /**
   * Delete an interview
   */
  static async deleteInterview(interviewId: string): Promise<boolean> {
    return InterviewUpdateService.deleteInterview(interviewId);
  }

  /**
   * Get a single interview by ID
   */
  static async getInterviewById(interviewId: string): Promise<Interview | null> {
    return InterviewFetchService.getInterviewById(interviewId);
  }

  /**
   * Get all interviews marked for the feeder
   */
  static async getFeederInterviews(): Promise<Interview[]> {
    return InterviewFetchService.getFeederInterviews();
  }

  /**
   * Mark an interview for the feeder queue
   */
  static async markForFeeder(interviewId: string): Promise<boolean> {
    return InterviewFetchService.markForFeeder(interviewId);
  }
}
